import {ALLOWED_REDIRECT_HOSTNAMES} from '@/constants';

/** @return {string} */
export function getRootDomain(domain) {
  if (domain == null || typeof domain !== 'string') return domain;

  const whitelist = [
    'author-p80902-e698666.adobeaemcloud.com',
    'author-p80902-e698666.adobeaemcloud.net',
    'author-p80902-e698665.adobeaemcloud.com',
    'author-p80902-e698665.adobeaemcloud.net',
    'author-p80902-e698709.adobeaemcloud.com',
    'author-p80902-e698709.adobeaemcloud.net',
  ];
  if (whitelist.includes(domain)) {
    return domain;
  }

  const root = domain.split('.').slice(1).join('.');
  return root;
}

/** Hosts that trigger the direct-checkout flow, bypassing geo-blocking. */
const DIRECT_CHECKOUT_HOSTS = new Set(['checkout.esri.com', 'checkoutstg.esri.com']);

/**
 * Checks if the hostname is explicitly allowed for redirects.
 * @param {*} hostname
 * @returns {boolean}
 */
export function isHostAllowed(hostname) {
  return ALLOWED_REDIRECT_HOSTNAMES.has(hostname);
}

/**
 * Validates the URL against the allowlist and returns the sanitized href
 * derived from a parsed URL object, or null if not allowed.
 * Returning parsed.href (not the raw input) breaks the taint chain for
 * static analysis tools that track user-controlled string flow.
 *
 * @param {string|null} url
 * @returns {string|null}
 */
export function sanitizeRedirectUrl(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    if (url.startsWith('/') && !url.startsWith('//')) {
      // Reconstruct via URL parsing to produce a non-tainted string
      const parsed = new URL(url, 'https://www.esri.com');
      return parsed.pathname + parsed.search + parsed.hash;
    }
    const parsed = new URL(url);
    const isHttpProtocol = parsed.protocol === 'https:' || parsed.protocol === 'http:';
    const hasUserInfo = Boolean(parsed.username) || Boolean(parsed.password);
    if (isHttpProtocol && !hasUserInfo && isHostAllowed(parsed.hostname)) {
      return parsed.href;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Returns true when the redirect URI targets a checkout host directly,
 * or when it targets an allowed host with a /checkout path.
 *
 * @param {string|null} redirectUri
 * @returns {boolean}
 */
export function isDirectCheckoutUrl(redirectUri) {
  try {
    const parsed = new URL(redirectUri);
    if (DIRECT_CHECKOUT_HOSTS.has(parsed.hostname)) return true;
    return isHostAllowed(parsed.hostname) && parsed.pathname.startsWith('/checkout');
  } catch {
    return false;
  }
}
